import { useCallback, useEffect, useRef, useState } from 'react';

export interface CountdownProps {
    startTime: number;
}

export interface Countdown {
    countdown: number;
    isRunning: boolean;
    start: () => void;
    reset: () => void;
}

export function useCountdown({ startTime }: CountdownProps): Countdown {
    const INTERVAL = 1000;
    const [countdown, setCountdown] = useState(startTime);
    const [canStart, setCanStart] = useState(false);
    const [isRunning, setIsRunning] = useState(false);

    function start() {
        setCanStart(true);
    }

    function stopTimer(timer: number) {
        setCanStart(false);
        setIsRunning(false);
        setCountdown(timer);
    }

    const reset = useCallback(() => {
        stopTimer(startTime);
    }, [startTime]);

    // Use a ref so this mutable variable does not cause a rerender
    const countdownRef = useRef<number>(startTime);

    // Only update the countdown ref when the actual count changes
    useEffect(() => {
        countdownRef.current = countdown;
    }, [countdown]);

    useEffect(() => {
        function tick() {
            if (countdownRef.current / INTERVAL <= 0) {
                reset();
            } else {
                setCountdown((t) => t - INTERVAL);
            }
        }

        let id: ReturnType<typeof setInterval>;
        if (canStart) {
            id = setInterval(tick, INTERVAL);
            if (!isRunning) {
                setIsRunning(true);
            }
        }
        return () => clearInterval(id);
    }, [reset, canStart, isRunning]);

    return {
        countdown,
        start,
        reset,
        isRunning,
    };
}
